import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  jobMonitoringService,
  JOB_STATUS_LABELS,
  type JobStatistics,
  type JobInfo,
  type PagedJobResult,
} from '../../services/jobMonitoringService';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  QueueListIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

const AUTO_REFRESH_INTERVAL = 15_000;

const STATUS_BADGE: Record<string, string> = {
  Processing: 'bg-blue-100 text-blue-800',
  Enqueued: 'bg-yellow-100 text-yellow-800',
  Failed: 'bg-red-100 text-red-800',
  Succeeded: 'bg-green-100 text-green-800',
  Scheduled: 'bg-purple-100 text-purple-800',
  Deleted: 'bg-gray-100 text-gray-500',
};

const statusOptions = Object.entries(JOB_STATUS_LABELS).map(([key, label]) => ({
  value: key,
  label,
}));

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' });
}

export const JobMonitoringPage: React.FC = () => {
  const [stats, setStats] = useState<JobStatistics | null>(null);
  const [jobs, setJobs] = useState<PagedJobResult | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [jobTypeFilter, setJobTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [jobTypeOptions, setJobTypeOptions] = useState<{ value: string; label: string }[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    jobMonitoringService.getJobTypeOptions().then(setJobTypeOptions).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, jobsRes] = await Promise.all([
        jobMonitoringService.getStatistics(),
        jobMonitoringService.getJobs({
          status: statusFilter || undefined,
          jobType: jobTypeFilter || undefined,
          page,
          pageSize,
        }),
      ]);
      setStats(statsRes);
      setJobs(jobsRes);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Erro ao carregar monitoramento de jobs:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, jobTypeFilter, page, pageSize]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoRefresh) {
      timerRef.current = setInterval(fetchData, AUTO_REFRESH_INTERVAL);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [autoRefresh, fetchData]);

  const handleRetry = async (id: string) => {
    try {
      await jobMonitoringService.retryJob(id);
      await fetchData();
    } catch {
      /* handled */
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await jobMonitoringService.deleteJob(id);
      await fetchData();
    } catch {
      /* handled */
    }
  };

  const handleFilterChange = () => {
    setPage(1);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Monitoramento de Jobs</h1>
          <p className="text-sm text-gray-500 mt-1">
            Acompanhe o processamento de tarefas do sistema em tempo real.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-gray-400">
              Atualizado: {lastRefresh.toLocaleTimeString('pt-BR')}
            </span>
          )}
          <button
            onClick={() => setAutoRefresh((v) => !v)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm transition-colors ${
              autoRefresh
                ? 'bg-green-50 text-green-700 hover:bg-green-100'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            <ClockIcon className="h-4 w-4" />
            {autoRefresh ? 'Auto 15s' : 'Pausado'}
          </button>
          <button
            onClick={() => { setLoading(true); fetchData(); }}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 text-sm transition-colors"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <KpiCard
            label="Em Processamento"
            value={stats.processing}
            icon={<ArrowPathIcon className="h-6 w-6 text-blue-600" />}
            bg="bg-blue-50"
            active={statusFilter === 'Processing'}
            activeBorder="ring-blue-400"
            onClick={() => {
              const next = statusFilter === 'Processing' ? '' : 'Processing';
              setStatusFilter(next);
              handleFilterChange();
            }}
          />
          <KpiCard
            label="Na Fila"
            value={stats.enqueued}
            icon={<QueueListIcon className="h-6 w-6 text-yellow-600" />}
            bg="bg-yellow-50"
            active={statusFilter === 'Enqueued'}
            activeBorder="ring-yellow-400"
            onClick={() => {
              const next = statusFilter === 'Enqueued' ? '' : 'Enqueued';
              setStatusFilter(next);
              handleFilterChange();
            }}
          />
          <KpiCard
            label="Agendados"
            value={stats.scheduled}
            icon={<ClockIcon className="h-6 w-6 text-purple-600" />}
            bg="bg-purple-50"
            active={statusFilter === 'Scheduled'}
            activeBorder="ring-purple-400"
            onClick={() => {
              const next = statusFilter === 'Scheduled' ? '' : 'Scheduled';
              setStatusFilter(next);
              handleFilterChange();
            }}
          />
          <KpiCard
            label="Falhos"
            value={stats.failed}
            icon={<XCircleIcon className="h-6 w-6 text-red-600" />}
            bg="bg-red-50"
            active={statusFilter === 'Failed'}
            activeBorder="ring-red-400"
            onClick={() => {
              const next = statusFilter === 'Failed' ? '' : 'Failed';
              setStatusFilter(next);
              handleFilterChange();
            }}
          />
          <KpiCard
            label="Concluidos"
            value={stats.succeeded}
            icon={<CheckCircleIcon className="h-6 w-6 text-green-600" />}
            bg="bg-green-50"
            active={statusFilter === 'Succeeded'}
            activeBorder="ring-green-400"
            onClick={() => {
              const next = statusFilter === 'Succeeded' ? '' : 'Succeeded';
              setStatusFilter(next);
              handleFilterChange();
            }}
          />
          <KpiCard
            label="Removidos"
            value={stats.deleted}
            icon={<TrashIcon className="h-6 w-6 text-gray-500" />}
            bg="bg-gray-50"
            active={statusFilter === 'Deleted'}
            activeBorder="ring-gray-400"
            onClick={() => {
              const next = statusFilter === 'Deleted' ? '' : 'Deleted';
              setStatusFilter(next);
              handleFilterChange();
            }}
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Tipo de Job</label>
            <select
              value={jobTypeFilter}
              onChange={(e) => { setJobTypeFilter(e.target.value); handleFilterChange(); }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Todos</option>
              {jobTypeOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); handleFilterChange(); }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Todos</option>
              {statusOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Jobs Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inicio</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duracao</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acoes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && !jobs ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center">
                    <ArrowPathIcon className="h-6 w-6 animate-spin mx-auto text-blue-500 mb-2" />
                    <span className="text-sm text-gray-500">Carregando...</span>
                  </td>
                </tr>
              ) : jobs && jobs.items.length > 0 ? (
                jobs.items.map((job) => (
                  <JobRow
                    key={job.id}
                    job={job}
                    onRetry={handleRetry}
                    onDelete={handleDelete}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-400">
                    Nenhum job encontrado para os filtros selecionados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {jobs && jobs.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-sm text-gray-500">
              {jobs.totalCount} registro(s) — Pagina {jobs.page} de {jobs.totalPages}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
              >
                Anterior
              </button>
              <button
                disabled={page >= jobs.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
              >
                Proximo
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function KpiCard({
  label,
  value,
  icon,
  bg,
  active,
  activeBorder,
  onClick,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  bg: string;
  active?: boolean;
  activeBorder?: string;
  onClick?: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); }}
      className={`rounded-lg p-4 ${bg} flex items-center gap-4 cursor-pointer transition-all select-none
        ${active ? `ring-2 ${activeBorder} shadow-md scale-[1.02]` : 'hover:shadow-sm hover:scale-[1.01]'}`}
    >
      <div className="shrink-0">{icon}</div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value.toLocaleString('pt-BR')}</p>
        <p className="text-xs text-gray-600">{label}</p>
      </div>
    </div>
  );
}

function JobRow({
  job,
  onRetry,
  onDelete,
}: {
  job: JobInfo;
  onRetry: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const badgeClass = STATUS_BADGE[job.state] ?? 'bg-gray-100 text-gray-600';

  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer"
        onClick={() => job.exceptionMessage && setExpanded((v) => !v)}
      >
        <td className="px-4 py-3 text-sm font-medium text-gray-900">
          {job.displayName || job.name}
        </td>
        <td className="px-4 py-3">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
            {JOB_STATUS_LABELS[job.state] ?? job.state}
          </span>
        </td>
        <td className="px-4 py-3 text-sm text-gray-500">{formatDate(job.startedAt || job.createdAt)}</td>
        <td className="px-4 py-3 text-sm text-gray-500">{job.duration ?? '-'}</td>
        <td className="px-4 py-3 text-right">
          <div className="flex justify-end gap-1">
            {job.state === 'Failed' && (
              <button
                onClick={(e) => { e.stopPropagation(); onRetry(job.id); }}
                title="Reprocessar"
                className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
              >
                <ArrowPathIcon className="h-4 w-4" />
              </button>
            )}
            {(job.state === 'Failed' || job.state === 'Enqueued' || job.state === 'Scheduled') && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(job.id); }}
                title="Remover"
                className="p-1.5 rounded hover:bg-red-50 text-red-600"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && job.exceptionMessage && (
        <tr>
          <td colSpan={5} className="px-4 py-3 bg-red-50">
            <div className="flex items-start gap-2">
              <ExclamationTriangleIcon className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
              <pre className="text-xs text-red-700 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
                {job.exceptionMessage}
              </pre>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default JobMonitoringPage;
